import { Coordinate, TileSize } from "game-common/models"
import { BLEND_MODES, Container, Graphics, LINE_CAP, Point, Sprite, Text } from "pixi.js"
import { clamp01, easeOutBack, easeOutCubic } from "../gui/chapter_reward_motion"
import { EntityHighlight, HighlightFrame } from "../sprites/entity_highlight"
import { TextRenderer } from "../text/text_renderer"

/**
 * What a held press on a map tile shows while it is held: the tile lit up, wearing the outline glow a pointed-at entity
 * wears, and a small ring above it that fills for as long as the hold must last, with the verb it will do beside it.
 * Letting go early drains the ring away; holding to the end closes it with a bloom as the verb fires.
 *
 * The press decides and this only draws. The clock is the client's own, because so is the hold: the verb is sent
 * when the press's timer runs out (see Player.beginTilePress).
 */

export interface TilePress {
    durationMs: number
    /** The tile's live sprites, asked every frame (a map rerender rebuilds them). None: `cell` is outlined instead. */
    art: () => Sprite[]
    /** The pressed cell's top-left in world-surface pixels, for a tile with no sprite of its own (a floor, a roof). */
    cell: Coordinate
    /** The verb a full hold does, written beside the ring. None: the hold opens a menu of verbs. */
    label?: string
}

/**
 * The cue's beats, in ms: wait out a click (one shorter than `delay` shows nothing), pop in, close with a bloom, and
 * drain away when released early.
 */
// Wait past ordinary click/tap durations before showing dismantle feedback or swinging.
export const TILE_PRESS_CUE_MS = { delay: 250, appear: 160, complete: 320, cancel: 170 }

// The dial is drawn in screen pixels against a 64px tile (`root` undoes the world surface's zoom), in the build
// countdown's colours: it is the same family of dial, over the same walls. No SCALER correction: the letterbox cancels
// the stage's vertical stretch, so the world surface is already square on screen.
const RADIUS = 11
const RING_RADIUS = RADIUS - 3
const RING_WIDTH = 3
const FACE_COLOR = 0x20262d
const BORDER_COLOR = 0x8e7b58
const GROOVE_COLOR = 0x454b54
const RING_COLOR = 0xffd166
const INK_COLOR = 0xffedc7
// The hover glow's amber, for the outline of a tile with no sprite to trace.
const FOOTPRINT_COLOR = 0xf7c070
// Space between the dial and its label, and between the cue and the top of the tile's art.
const LABEL_GAP = 4
const LIFT = 6
// Above the sky surface (1000000), so a roof's cue is not drawn under the roof.
const CUE_Z = 1000002
const FOOTPRINT_Z = CUE_Z - 1
const FOOTPRINT_FADE_IN_MS = 140
const FOOTPRINT_FADE_OUT_MS = 220
// The tile's art added onto itself. In a run of wall its neighbours hide the outline's sides, so this is what picks
// the pressed piece out of the run. It brightens as the hold fills, flares as the verb fires.
const LIT_FADE_IN_MS = 120
const LIT_FADE_OUT_MS = 220
const LIT_REST = 0.16
const LIT_FULL = 0.42
const LIT_FLASH = 0.7

type Phase = "idle" | "holding" | "complete" | "cancel"

export class TilePressCue {
    private press?: TilePress
    private phase: Phase = "idle"
    private startedAt = 0
    private endedAt = 0
    // How full the ring was when the hold was let go: the drain starts from there.
    private releasedAt = 0
    private readonly root = new Container()
    private readonly dial = new Graphics()
    private readonly bloom = new Graphics()
    private readonly label: Text
    private readonly footprint = new Graphics()
    private footprintStrength = 0
    private lit = new Container()
    private litStrength = 0
    private litGlow = LIT_REST
    private lastFrameAt = 0
    // What the outline traces, the tile's art as of this frame. Sprites that have since been destroyed drop out.
    private art: Sprite[] = []
    private readonly highlight = new EntityHighlight({
        artContainer: () => this.art[0]?.parent,
        frame: () => this.frames(),
        zIndex: () => Math.min(...this.art.map(sprite => sprite.zIndex)) - 1,
    })

    constructor(private worldSurface: () => Container | undefined) {
        this.root.zIndex = CUE_Z
        this.root.visible = false
        this.root.addChild(this.bloom, this.dial)
        this.label = new TextRenderer().fontSize(10).color(INK_COLOR).bold().strokeThickness(2).render("") as Text
        this.label.anchor.set(0, 0.5)
        this.root.addChild(this.label)
        this.footprint.zIndex = FOOTPRINT_Z
        this.footprint.visible = false
        this.drawFootprint()
    }

    isActive = () => this.phase !== "idle"

    begin = (press: TilePress, now = Date.now()) => {
        this.press = press
        this.phase = "holding"
        this.startedAt = now
        this.art = []
        this.label.text = press.label ?? ""
        this.label.visible = !!press.label
        this.animate(now)
    }

    /** The hold ran its course and its verb fired. */
    complete = (now = Date.now()) => {
        if (this.phase !== "holding") return
        this.phase = "complete"
        this.endedAt = now
        this.highlight.set(false)
    }

    /** The press ended. Before the hold was done, that throws it away; after, the completion plays on. */
    cancel = (now = Date.now()) => {
        if (this.phase !== "holding") return
        if (!this.shown(now)) { this.stop(); return }
        this.releasedAt = this.progress(now)
        this.phase = "cancel"
        this.endedAt = now
        this.highlight.set(false)
    }

    /** Called every frame. Cheap when idle. */
    animate = (now = Date.now()) => {
        const elapsed = this.lastFrameAt ? Math.max(0, now - this.lastFrameAt) : 0
        this.lastFrameAt = now
        if (this.phase === "idle") {
            this.fadeFootprint(elapsed, false)
            this.fadeLit(elapsed, false)
            return
        }
        const surface = this.worldSurface()
        if (!surface || surface.destroyed || !this.press) { this.stop(); return }
        if (this.root.parent !== surface) surface.addChild(this.root)
        if (this.footprint.parent !== surface) surface.addChild(this.footprint)

        this.art = this.press.art().filter(sprite => !sprite.destroyed && sprite.parent)
        const traced = this.art.length > 0
        if (this.phase === "holding" && traced && this.shown(now)) this.highlight.set(true)
        this.footprint.position.set(this.press.cell.x, this.press.cell.y)
        this.fadeFootprint(elapsed, this.phase === "holding" && !traced && this.shown(now))
        this.fadeLit(elapsed, this.litFor(now))

        const anchor = this.anchor(surface)
        this.root.position.set(anchor.x, anchor.y)
        if (!this.drawDial(now)) this.stop()
    }

    destroy = () => {
        if (this.root.destroyed) return
        this.stop()
        this.highlight.dispose()
        this.root.parent?.removeChild(this.root)
        this.root.destroy({ children: true })
        this.footprint.parent?.removeChild(this.footprint)
        this.footprint.destroy()
        this.lit.parent?.removeChild(this.lit)
        if (!this.lit.destroyed) this.lit.destroy({ children: true })
    }

    // The last traced art is kept: the outline fades out from it.
    private stop = () => {
        this.phase = "idle"
        this.press = undefined
        this.root.visible = false
        this.highlight.set(false)
    }

    private shown = (now: number) => now - this.startedAt >= TILE_PRESS_CUE_MS.delay

    private progress = (now: number) => clamp01((now - this.startedAt) / Math.max(1, this.press?.durationMs ?? 1))

    /** Draws the dial for this moment. False once its ending has played out. */
    private drawDial = (now: number): boolean => {
        if (!this.shown(now)) {
            this.root.visible = false
            return true
        }
        const appear = clamp01((now - this.startedAt - TILE_PRESS_CUE_MS.delay) / TILE_PRESS_CUE_MS.appear)
        let scale = 0.6 + 0.4 * easeOutBack(appear)
        let alpha = clamp01(appear * 1.6)
        let fill = this.progress(now)
        let flash = 0
        let bloom = -1
        if (this.phase === "complete") {
            const t = clamp01((now - this.endedAt) / TILE_PRESS_CUE_MS.complete)
            if (t >= 1) return false
            fill = 1
            flash = 1 - t
            bloom = t
            scale = 1 + 0.18 * easeOutCubic(t)
            alpha = 1 - clamp01((t - 0.3) / 0.7)
        } else if (this.phase === "cancel") {
            const t = clamp01((now - this.endedAt) / TILE_PRESS_CUE_MS.cancel)
            if (t >= 1) return false
            fill = this.releasedAt * (1 - easeOutCubic(t))
            scale *= 1 - 0.15 * t
            alpha *= 1 - t
        }
        this.root.visible = true
        this.root.alpha = alpha
        this.root.scale.set(0.5 * scale)

        // The dial and its label make one chip, centred over the tile and standing just clear of its art.
        const width = RADIUS * 2 + (this.label.visible ? LABEL_GAP + this.label.width : 0)
        const cx = -width / 2 + RADIUS
        const cy = -RADIUS - LIFT
        this.label.position.set(cx + RADIUS + LABEL_GAP, cy)

        const g = this.dial
        g.clear()
        // Dropped silhouette, so the dial reads as an object above the wall rather than a decal on it.
        g.beginFill(0x000000, 0.28).drawCircle(cx, cy + 2, RADIUS).endFill()
        g.lineStyle(1, flash > 0 ? INK_COLOR : BORDER_COLOR, 0.9)
        g.beginFill(FACE_COLOR, 0.97).drawCircle(cx, cy, RADIUS - 1).endFill()
        g.lineStyle(RING_WIDTH, GROOVE_COLOR, 0.9).drawCircle(cx, cy, RING_RADIUS)
        if (fill > 0) {
            // Filling, not draining: the hold is building toward something.
            const from = -Math.PI / 2
            const to = from + Math.min(1, fill) * Math.PI * 2
            const color = flash > 0 ? INK_COLOR : RING_COLOR
            // A fat faint copy under the arc is the glow; this hardware cannot spare a filter.
            g.lineStyle({ width: RING_WIDTH + 2.5, color, alpha: 0.26, cap: LINE_CAP.ROUND })
                .moveTo(cx, cy - RING_RADIUS).arc(cx, cy, RING_RADIUS, from, to)
            g.lineStyle({ width: RING_WIDTH, color, alpha: 1, cap: LINE_CAP.ROUND })
                .moveTo(cx, cy - RING_RADIUS).arc(cx, cy, RING_RADIUS, from, to)
            if (fill < 1) {
                // The moving head: a ring barely started still reads as moving.
                g.lineStyle(0).beginFill(INK_COLOR)
                    .drawCircle(cx + Math.cos(to) * RING_RADIUS, cy + Math.sin(to) * RING_RADIUS, RING_WIDTH / 2 + 0.4).endFill()
            }
        }
        if (flash > 0) {
            // The closing ring lights the whole face for a moment.
            g.lineStyle(0).beginFill(RING_COLOR, 0.45 * flash).drawCircle(cx, cy, RING_RADIUS - RING_WIDTH / 2).endFill()
        }

        // The success bloom starts OUTSIDE the rim, so it can never be read as the gauge itself.
        const b = this.bloom
        b.clear()
        if (bloom >= 0) {
            const radius = RADIUS + 1 + 10 * easeOutCubic(bloom)
            b.lineStyle(2.5 * (1 - bloom) + 0.5, RING_COLOR, Math.pow(1 - bloom, 1.5)).drawCircle(cx, cy, radius)
        }
        return true
    }

    /** The point the cue stands on: the top centre of the tile's art (or the pressed cell), in world-surface pixels. */
    private anchor = (surface: Container): Coordinate => {
        if (!this.art.length) return { x: this.press.cell.x + TileSize / 2, y: this.press.cell.y }
        let minX = Infinity, maxX = -Infinity, minY = Infinity
        this.frames().forEach(({ texture, x, y, scaleX, scaleY, anchorX = 0, anchorY = 0 }) => {
            const w = texture.width * scaleX, h = texture.height * scaleY
            const left = x - anchorX * w, top = y - anchorY * h
            minX = Math.min(minX, left, left + w)
            maxX = Math.max(maxX, left, left + w)
            minY = Math.min(minY, top, top + h)
        })
        const container = this.art[0].parent
        const top = new Point((minX + maxX) / 2, minY)
        return container === surface ? top : surface.toLocal(top, container)
    }

    private frames = (): HighlightFrame[] => this.art
        .filter(sprite => !sprite.destroyed && sprite.texture)
        .map(sprite => ({
            texture: sprite.texture, x: sprite.x, y: sprite.y, scaleX: sprite.scale.x, scaleY: sprite.scale.y,
            anchorX: sprite.anchor.x, anchorY: sprite.anchor.y,
        }))

    /** Whether the tile should be lit at this moment, setting how brightly. */
    private litFor = (now: number): boolean => {
        if (this.phase === "holding") {
            this.litGlow = LIT_REST + (LIT_FULL - LIT_REST) * this.progress(now)
            return this.shown(now)
        }
        if (this.phase === "complete") {
            const t = clamp01((now - this.endedAt) / TILE_PRESS_CUE_MS.complete)
            this.litGlow = LIT_FULL + (LIT_FLASH - LIT_FULL) * (1 - t)
            return t < 0.5
        }
        return false
    }

    // Additive copies of the art, laid just over it in its own container, so they sort with it.
    private fadeLit = (elapsed: number, on: boolean) => {
        const step = elapsed / (on ? LIT_FADE_IN_MS : LIT_FADE_OUT_MS)
        this.litStrength = on ? Math.min(1, this.litStrength + step) : Math.max(0, this.litStrength - step)
        // A container torn down with its children (another storey leaving the screen) took these copies with it.
        if (this.lit.destroyed) this.lit = new Container()
        const art = this.art.filter(sprite => !sprite.destroyed && sprite.parent && !sprite.parent.destroyed)
        if (this.litStrength <= 0 || !art.length) {
            this.lit.visible = false
            return
        }
        const container = art[0].parent
        if (this.lit.parent !== container) container.addChild(this.lit)
        this.lit.zIndex = Math.max(...art.map(sprite => sprite.zIndex)) + 0.01
        art.forEach((sprite, i) => {
            let copy = this.lit.children[i] as Sprite
            if (!copy) {
                copy = new Sprite()
                copy.blendMode = BLEND_MODES.ADD
                this.lit.addChild(copy)
            }
            copy.texture = sprite.texture
            copy.position.copyFrom(sprite.position)
            copy.scale.copyFrom(sprite.scale)
            copy.anchor.copyFrom(sprite.anchor)
            copy.visible = true
        })
        for (let i = art.length; i < this.lit.children.length; i++) this.lit.children[i].visible = false
        this.lit.alpha = this.litStrength * this.litGlow
        this.lit.visible = true
    }

    private fadeFootprint = (elapsed: number, on: boolean) => {
        const step = elapsed / (on ? FOOTPRINT_FADE_IN_MS : FOOTPRINT_FADE_OUT_MS)
        this.footprintStrength = on ? Math.min(1, this.footprintStrength + step) : Math.max(0, this.footprintStrength - step)
        this.footprint.alpha = this.footprintStrength
        this.footprint.visible = this.footprintStrength > 0
    }

    // A lit rim just inside the cell: the same amber as the hover outline, for a tile with no shape to trace.
    private drawFootprint = () => {
        const g = this.footprint
        g.clear()
        g.lineStyle(3.5, FOOTPRINT_COLOR, 0.22).drawRoundedRect(1.5, 1.5, TileSize - 3, TileSize - 3, 4)
        g.lineStyle(1.25, FOOTPRINT_COLOR, 0.95).drawRoundedRect(1.5, 1.5, TileSize - 3, TileSize - 3, 4)
        g.lineStyle(0).beginFill(FOOTPRINT_COLOR, 0.12).drawRoundedRect(2, 2, TileSize - 4, TileSize - 4, 3.5).endFill()
    }
}
